import type { Role, Permission } from '../generated/types';
import type { AnyRole } from './role-typeguards';

export interface PermissionMetadata {
    title: string;
    description: string | null;
}

export enum RoleType {
    User = 'User',
    Team = 'Team',
    Client = 'Client',
}

export interface RoleMetadata {
    title: string;
    description: string | null;
    isImplicit: boolean;
    isSuperUser: boolean;
    roleType: RoleType;
    badge: null | {
        label: string;
        color: string;
    };
}

export type RbacMetadata = Readonly<{
    permissionCount: number;
    permissions: Readonly<Record<Permission, PermissionMetadata>>;
    roleCount: number;
    roles: Readonly<Record<Role, RoleMetadata>>;
}>;

export async function getRbacMetadata(): Promise<RbacMetadata> {
    const module = await import('../generated/rbac-metadata.json');
    return module.default as RbacMetadata;
}

export async function getRoleMetadata(role: AnyRole): Promise<RoleMetadata> {
    const metadata = await getRbacMetadata();
    return (
        metadata.roles[role] ?? {
            roleType: RoleType.Client,
            isSuperUser: false,
            isImplicit: true,
            title: role,
            description: null,
            badge: null,
        }
    );
}

export async function getPermissionMetadata(
    permission: Permission
): Promise<PermissionMetadata> {
    const metadata = await getRbacMetadata();
    return (
        metadata.permissions[permission] ?? {
            title: permission,
            description: null,
        }
    );
}

export async function getPermissions(): Promise<Permission[]> {
    const metadata = await getRbacMetadata();
    return Object.keys(metadata.permissions) as Permission[];
}
